import React from 'react';
import { Box, Typography } from '@mui/material';
import TopNav from './TopNav.jsx';
import Footer from './Footer.jsx';
import useDocumentTitle from '../utils/useDocumentTitle';
import { PALETTE } from '../theme';

/**
 * Shared chrome for low-traffic informational pages (About, Privacy, Shipping).
 * Renders TopNav + a centered prose column + Footer. Children are the body.
 */
// `description` is still accepted from callers (About/Privacy/Shipping) but is
// no longer rendered per-page — site-wide SEO meta lives in public/index.html.
const StaticPage = ({ title, heading, intro, children }) => {
    useDocumentTitle(title);
    return (
    <Box
        sx={{
            minHeight: '100vh',
            '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
            backgroundColor: PALETTE.page,
            display: 'flex',
            flexDirection: 'column'
        }}
    >

        <TopNav />

        <Box component="main" sx={{ flex: 1, py: { xs: 5, sm: 7 }, px: { xs: 2, sm: 3 } }}>
            <Box sx={{ maxWidth: 760, mx: 'auto' }}>
                <Typography
                    component="h1"
                    sx={{
                        fontSize: { xs: '2rem', sm: '2.75rem' },
                        fontWeight: 800,
                        color: PALETTE.text,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.1,
                        mb: 2
                    }}
                >
                    {heading}
                </Typography>
                {intro && (
                    <Typography
                        sx={{
                            color: PALETTE.textBody,
                            fontSize: { xs: '1.05rem', sm: '1.15rem' },
                            lineHeight: 1.55,
                            mb: 4
                        }}
                    >
                        {intro}
                    </Typography>
                )}

                <Box
                    sx={{
                        '& h2': {
                            fontSize: { xs: '1.25rem', sm: '1.4rem' },
                            fontWeight: 700,
                            color: PALETTE.text,
                            mt: 4,
                            mb: 1.25,
                            letterSpacing: '-0.01em'
                        },
                        '& p, & ul': {
                            color: PALETTE.textBody,
                            fontSize: '1rem',
                            lineHeight: 1.7,
                            mb: 2
                        },
                        '& ul': { pl: 3 },
                        '& li': { mb: 0.5 },
                        '& a': {
                            color: PALETTE.primaryDark,
                            textDecoration: 'none',
                            fontWeight: 500,
                            '&:hover': { textDecoration: 'underline' }
                        }
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>

        <Footer />
    </Box>
    );
};

export default StaticPage;
